import { AccountType } from '@entries/contentful-subscription-form/types'
import { type FormInput } from '@entries/contentful-subscription-form/types'
import { fetchInstance } from '@utils/fetch'

// Fixme récupérer le model de shift dès qu'ils auront migré sur CodeArtifact
type Product = any
enum PaymentMode {
  UNKNOWN = '',
  INTERNAL_TRANSFER = 'VIREMENT_INTERNE',
  EXTERNAL_TRANSFER = 'VIREMENT_EXTERNE',
  CHEQUE = 'CHEQUE',
  BANK_CARD = 'CARTE_BANCAIRE',
  FUND_TRANSFER = 'TRANSFERT_FONDS',
  DIRECT_DEBIT = 'PRELEVEMENT',
}
type Subscription = any

const ApiRoutes = {
  Postcode: '/_services/location/towns/postcode',
  Subscription: '/_services/subscription/content',
  Progress: '/_services/progress',
  MortgageMonthly: '/api/mortgage-v2/taux-mensualites',
  MortgageCapacity: '/api/mortgage-v2/capacite-emprunt',
}

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'x-origin': 'website',
  'x-team': 'inbound',
}

type ISubscriptionBody = FormInput & {
  codePromo?: string
  from?: string
  codePub?: string
  vpDefaultSavings?: boolean
  partnerName?: string
  partnerMemberId?: string
}

export const updateSubscription = async (id: string) =>
  fetchInstance(`${ApiRoutes.Progress}/${id}`, {
    method: 'put',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      step: 'choix-carte',
      version: '1.0',
      product: 'cco',
    }),
  })

type SubscriptionExtended = Omit<Subscription, 'products'> & {
  products: Array<
    | (Product & {
        overdraft: boolean
        insuranceAdvice: boolean
        cardInsuranceHolder: boolean
        cardInsuranceCoHolder: boolean
      })
    | Pick<Product, 'id' | 'initialPayment'>
  >
}

export const createSubscription = async ({
  civility,
  firstName,
  lastName,
  tel,
  birthDate,
  email,
  postalCode,
  accountType,
  codePromo,
  codePub,
  vpDefaultSavings,
  partnerMemberId,
  partnerName,
}: ISubscriptionBody) => {
  const body: Omit<SubscriptionExtended, 'id'> = {
    origin: 'RE',
    partnerMemberId,
    partnerName,
    products: [
      {
        id: 'current-account',
        subscriptionMode: accountType === AccountType.JOINT ? 'JOINT' : 'INDIV',
        overdraft: false,
        insuranceAdvice: false,
        cardInsuranceHolder: false,
        cardInsuranceCoHolder:
          accountType === AccountType.JOINT ? false : undefined,
        initialPayment: { amount: 300 },
      },
    ],
    completeness: false,
    users: [
      {
        id: 'main',
        title: civility,
        firstName,
        legalCapacityCode: 'MAJEUR_CAPABLE',
        legalEntity: 'P',
        surname: lastName,
        mobilePhone: tel,
        dateOfBirth: birthDate,
        email,
        addresses: [
          {
            id: 'postal',
            countryCode: 'FR',
            postCode: postalCode,
          },
        ],
      },
    ],
    payment: {
      sourceType: PaymentMode.EXTERNAL_TRANSFER,
    },
    withdrawalPeriod: false,
  }

  if (codePromo && String(codePromo).length > 0) {
    body.promotionalCode = codePromo
  }

  if (codePub && String(codePub).length > 0) {
    body.users[0].adWording = codePub
  }

  if (vpDefaultSavings) {
    body.products.push({
      id: 'plus-savings-account',
      initialPayment: {
        amount: 10,
      },
    })
    body.payment = {
      sourceType: PaymentMode.EXTERNAL_TRANSFER,
    }
  }

  if (accountType === AccountType.JOINT) {
    body.users.push({
      id: 'secondary',
      legalCapacityCode: 'MAJEUR_CAPABLE',
      addresses: [{ id: 'postal', countryCode: 'FR' }],
    })
  }

  return fetchInstance(`${ApiRoutes.Subscription}`, {
    headers,
    credentials: 'include',
    method: 'post',
    body: JSON.stringify(body),
  })
}

export type GetMortgageCapacityResponse = {
  rate: number
  montantCapaciteAchat: number
}

export const getMortgageCapacity = async (
  duration: number,
  income: number,
  montantCharges: number,
  monthlyPaymentAmount: number
) =>
  fetchInstance(ApiRoutes.MortgageCapacity, {
    headers,
    method: 'post',
    body: JSON.stringify({
      duration,
      income,
      montantCharges,
      monthlyPaymentAmount,
      nbPersonneFoyer: 1,
    }),
  })

export type GetMortgageMonthlyResponse = {
  rate: number
  monthlyPaymentAmount: number
}

export const getMortgageMonthly = async (
  duration: number,
  income: number,
  montantApport: number,
  projectAmount: number
) =>
  fetchInstance(ApiRoutes.MortgageMonthly, {
    headers,
    method: 'post',
    body: JSON.stringify({
      duration,
      income,
      montantApport,
      projectAmount,
      nbPersonneFoyer: 1,
    }),
  })
